// ==========================================
// Asset URLs
// ==========================================
export const QUINNY_AI_AVATAR_URL =
  'https://cdn.qne.cloud/qca/assets/img/quinny-ai/quinny-logo-80.webp';
/** Larger Quinny mark (WebP); How-To banner (`quinny-logo-160.webp`). */
export const QUINNY_AI_LOGO_TRANSPARENT_URL =
  'https://cdn.qne.cloud/qca/assets/img/quinny-ai/quinny-logo-160.webp';
export const USER_AVATAR_URL =
  'https://cdn.qne.cloud/qca/assets/img/avatar_icon.png';

// ==========================================
// Country Configuration
// ==========================================
export const COUNTRY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'MY', label: 'Malaysia' },
  { value: 'MM', label: 'Myanmar' },
  { value: 'PH', label: 'Philippines' }
];

export const PHILIPPINES_COUNTRY_CODE = 'PH';
export const MALAYSIA_COUNTRY_CODE = 'MY';

/**
 * Detail-line W/Tax columns: same gate as W/Tax Codes (`/tax/wtaxcode`) — AR classic module + data permission.
 * Align with `wtaxcode` menu/data-permissions (`m2.16`, `wtaxcode.view`).
 */
export const ACL_WTAX_LINE_COLUMNS = {
  ability: ['m2.16', 'wtaxcode.view'],
  mode: 'allOf' as const
};

/** sessionStorage key: tenant GUID string; set when creating a company from My Companies so setup wizard step 1 locks country. */
export const SESSION_SETUP_COUNTRY_LOCKED_TENANT_KEY = 'qne_setup_country_locked_tenant';

// Countries that use "Statement of Financial Position" and "Statement of Comprehensive Income"
// instead of "Balance Sheet" and "Profit and Loss"
export const COUNTRIES_USING_STATEMENT_FORMAT = [
  'PH',
  'KH',
  'HK',
  'ID',
  'LA',
  'MV',
  'PG',
  'SG',
  'TH',
  'TL'
];

export function usesStatementFormat(
  country: string | null | undefined
): boolean {
  return country ? COUNTRIES_USING_STATEMENT_FORMAT.includes(country) : false;
}

export const INDUSTRY_OPTIONS: Array<{ value: number; label: string }> = [
  { label: 'Agriculture and Farming', value: 0 },
  {
    label: 'Bookkeeping / Accounting Services',
    value: 1
  },
  {
    label: 'Construction',
    value: 2
  },
  {
    label: 'Broadcasting / Entertainment/ Media',
    value: 3
  },
  {
    label: 'Food and Beverage/ Restaurant',
    value: 4
  },
  {
    label: 'Education and Healthcare Services',
    value: 5
  },
  {
    label: 'IT / Computers',
    value: 6
  },
  {
    label: 'Electronics / Telecommunication',
    value: 7
  },
  { label: 'Manufacturing', value: 8 },
  { label: 'Mining', value: 9 },
  { label: 'Pharmaceutical', value: 10 },
  {
    label: 'Professional Services (Marketing, Consultations, etc.)',
    value: 11
  },
  {
    label: 'Real Estate Services',
    value: 12
  },
  {
    label: 'Trading / Distribution',
    value: 13
  },
  {
    label: 'Transportation and Logistics',
    value: 14
  },
  { label: 'Others', value: 15 }
];

export const BUSINESS_OPTIONS: Array<{ value: number; label: string }> = [
  { label: 'Sole Proprietors/Professionals', value: 0 },
  { label: 'Mixed Income Earners', value: 1 },
  { label: 'Corporation', value: 2 },
  { label: 'Partnership', value: 3 },
  { label: 'Cooperatives/ Associations', value: 4 },
  { label: 'NGA/LGU/Government Owned and Controlled Corporation', value: 5 },
  { label: 'Non-Profit Organization', value: 6 },
  { label: 'Others', value: 7 }
];

export const MY_BUSINESS_OPTIONS: Array<{ value: number; label: string }> = [
  { label: 'Corporation (Bhd / Sdn Bhd)', value: 2 },
  { label: 'Sole Proprietorship', value: 0 },
  { label: 'Individual', value: 8 },
  { label: 'Non-Profit Organization', value: 6 },
  { label: 'Partnership', value: 3 }
];

export const TAX_TYPES_MY: Array<{ value: number; label: string }> = [
  { label: 'Standard-rated', value: 0 },
  { label: 'Zero-rated', value: 1 },
  { label: 'Exempt', value: 2 },
  { label: 'Out-of-scope', value: 3 },
  { label: 'N/A', value: 4 }
];

export const TAX_TYPES_PH: Array<{ value: number; label: string }> = [
  { label: 'Standard-rated', value: 0 },
  { label: 'Zero-rated', value: 1 },
  { label: 'Exempt', value: 2 },
  { label: 'Non-VAT', value: 3 },
  { label: 'N/A', value: 4 }
];

export const GOV_TAX_CODES_MY: Array<{ value: string; desc: string }> = [
  { value: 'NA', desc: 'N/A' },
  { value: 'ST', desc: 'Sales Tax (SST)' },
  { value: 'SVT', desc: 'Service Tax (SST)' },
  { value: 'PT', desc: 'Purchase Tax (SST)' },
  { value: 'ESVT', desc: 'Expenses Service Tax (SST)' },
  { value: 'STGEX', desc: 'Sales Tax (Goods Exempted From Tax)' },
  { value: 'STEP', desc: 'Sales Tax (Export)' },
  { value: 'STDA', desc: 'Sales Tax (Designated Area)' },
  { value: 'STSA', desc: 'Sales Tax (Special Area)' },
  {
    value: 'STPA',
    desc: 'Sales Tax (Persons Exempted From Payment Of Tax-Schedule A)'
  },
  {
    value: 'STPB',
    desc: 'Sales Tax (Persons Exempted From Payment Of Tax-Schedule B)'
  },
  {
    value: 'STPC1',
    desc: 'Sales Tax (Persons Exempted From Payment Of Tax-Schedule C Item No.1)'
  },
  {
    value: 'STPC2',
    desc: 'Sales Tax (Persons Exempted From Payment Of Tax-Schedule C Item No.2)'
  },
  {
    value: 'STPC3',
    desc: 'Sales Tax (Persons Exempted From Payment Of Tax-Schedule C Item No.3)'
  },
  {
    value: 'STPC4',
    desc: 'Sales Tax (Persons Exempted From Payment Of Tax-Schedule C Item No.4)'
  },
  {
    value: 'STPC5',
    desc: 'Sales Tax (Persons Exempted From Payment Of Tax-Schedule C Item No.5)'
  },
  { value: 'PTEX', desc: 'Purchase Tax (Goods Exempted From Tax)' },
  {
    value: 'PTPC1',
    desc: 'Purchase Tax (Persons Exempted From Payment Of Tax-Schedule C Item No.1)'
  },
  {
    value: 'PTPC2',
    desc: 'Purchase Tax (Persons Exempted From Payment Of Tax-Schedule C Item No.2)'
  },
  {
    value: 'PTPC3',
    desc: 'Purchase Tax (Persons Exempted From Payment Of Tax-Schedule C Item No.3)'
  },
  {
    value: 'PTPC4',
    desc: 'Purchase Tax (Persons Exempted From Payment Of Tax-Schedule C Item No.4)'
  },
  {
    value: 'PTPC5',
    desc: 'Purchase Tax (Persons Exempted From Payment Of Tax-Schedule C Item No.5)'
  },
  { value: 'STU', desc: 'Sales Tax (SST) – Goods For Own Used/Disposed' },
  { value: 'SVTU', desc: 'Service Tax (SST) – Service For Own Use' },
  { value: 'SVTD', desc: 'Service Tax With Payment Basis (SST)' },
  {
    value: 'SVTE',
    desc: 'Exemption From Payment Of Service Tax Under The Service Tax (Persons Exempted From Payment Of Tax) Order 2018'
  },
  { value: 'SVTEGR', desc: 'Exemption under Intra-group relief' },
  { value: 'SVTEOTH', desc: 'Other Exemption' },
  { value: 'SVTNTS', desc: 'Non-Taxable Services' },
  { value: 'IMSVTE', desc: 'Service Tax (Imported Services Exempted)' },
  { value: 'IMSVT', desc: 'Service Tax (Imported Services)' },
  {
    value: 'SVTDE',
    desc: 'Exemption From Payment Of Service Tax Under The Service Tax (Persons Exempted From Payment Of Tax) Order 2018 (By Payment Basis)'
  },
  { value: 'SVTSA', desc: 'Service Tax (Special Area)' },
  {
    value: 'SVTRL',
    desc: 'Service Tax For Rental/Lease Services with 2% Service Tax Exemption'
  },
  {
    value: 'SVTDRL',
    desc: 'Service Tax For Rental/Lease Services with 2% Service Tax Exemption (Payment Basis)'
  }
];

export const GOV_TAX_CODES_PH: Array<{ value: string; desc: string }> = [
  { value: 'NA', desc: 'I/O VAT is Not Applicable' },
  { value: 'SR_SP', desc: 'Vatable Sales/Receipt - Private' },
  { value: 'SR_SG', desc: 'Sales to Government' },
  { value: 'SR_PCG', desc: 'Purchase of Capital Goods' },
  {
    value: 'SR_PNCD',
    desc: 'Domestic Purchases of Goods other than Capital Goods'
  },
  { value: 'SR_PNCI', desc: 'Importation of Goods other than Capital Goods' },
  { value: 'SR_PSD', desc: 'Domestic Purchases of Services' },
  { value: 'SR_PSI', desc: 'Services rendered by Non-Residents' },
  { value: 'ZR_S', desc: 'Zero Rated Sales/Receipts' },
  { value: 'EX_S', desc: 'Exempt Sales/Receipts' },
  { value: 'ZR_P', desc: 'Purchases not Qualified for Input Tax' },
  { value: 'OS', desc: 'Out-of-scope Sales/Receipts' },
  { value: 'OP', desc: 'Out-of-scope Purchases' },
  { value: 'EX_P', desc: 'Exempt Purchases' },
  { value: 'ZR_PS', desc: 'Purchases not Qualified for Input Tax (Services)' },
  { value: 'EX_PI', desc: 'Exempt Purchases (Importation of Goods)' },
  { value: 'SR_SPS', desc: 'Vatable Sales/Receipt - Private (Services)' },
  { value: 'SR_SGS', desc: 'Sales to Government (Services)' },
  { value: 'EX_SS', desc: 'Exempt Sales/Receipts' },
  { value: 'ZR_SS', desc: 'Zero Rated Sales/Receipts (Services)' }
];

export const EINVOICE_TINTYPE: Array<{
  value: number;
  idType: string;
  label: string;
}> = [
  { value: 1, idType: 'BRN', label: 'Malaysian Company' },
  { value: 2, idType: 'NRIC', label: 'Malaysian Individual' },
  { value: 5, idType: 'BRN', label: 'Foreign Company' },
  { value: 4, idType: 'PASSPORT', label: 'Foreign Individual' },
  { value: 3, idType: 'NA', label: 'General Public/Consolidated E-Invoice' },
  { value: 6, idType: 'BRN', label: 'Government' }
];

export const EINVOICE_AP_TINTYPE: Array<{
  value: number;
  idType: string;
  label: string;
}> = [
  { value: 1, idType: 'BRN', label: 'Malaysian Company' },
  { value: 4, idType: 'PASSPORT', label: 'Foreign Company' },
  { value: 2, idType: 'NRIC', label: 'Malaysian Individual' },
  { value: 3, idType: 'PASSPORT', label: 'Foreign Individual' }
];

export const EINVOICE_TAXTYPE: Array<{ value: string; name: string }> = [
  { value: '01', name: 'Sales Tax' },
  { value: '02', name: 'Service Tax' },
  { value: '03', name: 'Tourism Tax' },
  { value: '04', name: 'High-Value Goods Tax' },
  { value: '05', name: 'Sales Tax on Low Value Goods' },
  { value: '06', name: 'Not Applicable' },
  { value: 'E', name: 'Tax Exemption (where applicable)' }
];

export const EINVOICE_TRANX_TYPE: Array<{ value: number; label: string }> = [
  // { value: 0, label: 'NA' },
  { value: 1, label: 'Agents, dealers, distributors, etc' },
  { value: 2, label: 'Foreign purchases (foreign suppliers)' },
  { value: 3, label: 'Profit distribution' },
  { value: 4, label: 'E-commerce transactions (merchant/service providers)' },
  { value: 5, label: 'Pay-Out to betting and gaming (winners)' },
  {
    value: 6,
    label: 'Transactions with individual taxpayers not conducting business'
  },
  // { value: 7, label: 'Recipient of the payment/credit' },
  { value: 8, label: 'Interest payment' },
  {
    value: 9,
    label:
      'Claim, compensation or benefit payments to Policyholder / Beneficiary'
  },
  { value: 10, label: 'Payment in relation to capital reduction (Investor)' }
];

export const EINVOICE_BILLING_FREQ: Array<{ value: string; label: string }> = [
  { value: '01', label: 'Daily' },
  { value: '02', label: 'Weekly' },
  { value: '03', label: 'Biweekly' },
  { value: '04', label: 'Monthly' },
  { value: '05', label: 'Bimonthly' },
  { value: '06', label: 'Quarterly' },
  { value: '07', label: 'Half-yearly' },
  { value: '08', label: 'Yearly' },
  { value: '09', label: 'Others/Not Applicable' }
];

export const GENDERS: Array<{ value: number; label: string }> = [
  { value: 0, label: 'Male' },
  { value: 1, label: 'Female' }
];

export const CUSTOMER_STATUS: Array<{ value: number; label: string }> = [
  { value: 0, label: 'ACTIVE' },
  { value: 1, label: 'INACTIVE' },
  { value: 2, label: 'PROSPECT' },
  { value: 3, label: 'SUSPENDED' }
];

export const SUPPLIER_STATUS: Array<{ value: number; label: string }> = [
  { value: 0, label: 'ACTIVE' },
  { value: 1, label: 'INACTIVE' },
  { value: 2, label: 'SUSPENDED' }
];

export const VPP_RULES: Array<{ value: number; label: string }> = [
  { value: 1, label: 'Block' },
  { value: 2, label: 'Allow with Warning' },
  { value: 3, label: 'Allow with Password' }
];

export const TAXABLE_QUARTERS: Array<{ value: number; label: string }> = [
  { value: 1, label: '1st Quarter' },
  { value: 2, label: '2nd Quarter' },
  { value: 3, label: '3rd Quarter' },
  { value: 4, label: '4th Quarter' }
];

export const VATRELIEF_TRANX_TYPES: Array<{ value: number; label: string }> = [
  { value: 1, label: 'Purchases' },
  { value: 2, label: 'Sales' },
  { value: 3, label: 'Importation' }
];

export const MONTHS: Array<{ value: number; label: string }> = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' }
];

export const RDO: Array<{ value: number; label: string; name: string }> = [
  { value: 1, label: 'RDO064', name: '064: Talisay, Camarines Norte' },
  { value: 2, label: 'RDO110', name: '110: General Santos City' },
  { value: 3, label: 'RDO070', name: '070: Masbate, Masbate' },
  { value: 4, label: 'RDO055', name: '055: San Pablo City' },
  { value: 5, label: 'RDO076', name: '076: Victorias, Northern Negros Occ.' },
  { value: 6, label: 'RDO104', name: '104: Bayugan, Agusan del Sur' },
  { value: 7, label: 'RDO93A', name: '93A: Zamboanga City' },
  { value: 8, label: 'RDO059', name: '059: Lipa City' },
  { value: 9, label: 'RDO017', name: '017: Tarlac, Tarlac' },
  { value: 10, label: 'RDO048', name: '048: West Makati' },
  { value: 11, label: 'RDO030', name: '030: Binondo' },
  { value: 12, label: 'RDO015', name: '015: Naguilan, Isabela' },
  { value: 13, label: 'RDO013', name: '013: Tuguegarao, Cagayan' },
  { value: 14, label: 'RDO23B', name: '23B: South Nueva Ecija' },
  { value: 15, label: 'RDO17A', name: '17A: Tarlac, Tarlac City' },
  { value: 16, label: 'RDO086', name: '086: Borongan, Eastern Samar' },
  { value: 17, label: 'RDO090', name: '090: Maasin, Southern Leyte' },
  { value: 18, label: 'RDO012', name: '012: Lagawe, Ifugao' },
  { value: 19, label: 'RDO065', name: '065: Naga City' },
  { value: 20, label: 'RDO096', name: '096: Bongao, Tawi-Tawi' },
  { value: 21, label: 'RDO002', name: '002: Vigan, Ilocos Sur' },
  { value: 22, label: 'RDO039', name: '039: South, Quezon City' },
  { value: 23, label: 'RDO103', name: '103: Butuan City' },
  { value: 24, label: 'RDO052', name: '052: Paranaque' },
  { value: 25, label: 'RDO037', name: '037: San Jose, Occidental Mindoro' },
  { value: 26, label: 'RDO028', name: '028: Novaliches' },
  { value: 27, label: 'RDO093', name: '093: Zamboanga City' },
  { value: 28, label: 'RDO026', name: '026: Malabon-Navotas' },
  { value: 29, label: 'RDO027', name: '027: Caloocan City' },
  { value: 30, label: 'RDO072', name: '072: Roxas City' },
  { value: 31, label: 'RDO100', name: '100: Ozamis City' },
  { value: 32, label: 'RDO042', name: '042: San Juan' },
  { value: 33, label: 'RDO050', name: '050: South Makati' },
  { value: 34, label: 'RDO057', name: '057: Biñan, Laguna' },
  { value: 35, label: 'RDO036', name: '036: Puerto Princesa' },
  { value: 36, label: 'RDO085', name: '085: Catarman, Northern Samar' },
  { value: 37, label: 'RDO106', name: '106: Tandag, Surigao del Sur' },
  { value: 38, label: 'RDO035', name: '035: Romblon' },
  { value: 39, label: 'RDO094', name: '094: Isabela, Basilan' },
  { value: 40, label: 'RDO016', name: '016: Cabarroguis, Quirino' },
  { value: 41, label: 'RDO101', name: '101: Iligan City' },
  { value: 42, label: 'RDO105', name: '105: Surigao City' },
  { value: 43, label: 'RDO074', name: '074: Iloilo City' },
  { value: 44, label: 'RDO006', name: '006: Urdaneta, East Pangasinan' },
  { value: 45, label: 'RDO43B', name: '43B: West Pasig' },
  { value: 46, label: 'RDO091', name: '091: Dipolog City' },
  { value: 47, label: 'RDO053', name: '053: Las Pinas-Muntinlupa' },
  { value: 48, label: 'RDO062', name: '062: Boac, Marinduque' },
  { value: 49, label: 'RDO024', name: '024: Valenzuela' },
  { value: 50, label: 'RDO004', name: '004: Calasiao, West Pangasinan' },
  { value: 51, label: 'RDO019', name: '019: Subic Bay Freeport Zone' },
  {
    value: 52,
    label: 'RDO122',
    name: '122: Makati Large Taxpayers District Office'
  },
  { value: 53, label: 'RDO079', name: '079: Dumaguete City' },
  { value: 54, label: 'RDO25B', name: '25B: Sta. Maria, Bulacan' },
  { value: 55, label: 'RDO003', name: '003: San Fernando, La Union' },
  { value: 56, label: 'RDO010', name: '010: Bontoc, Mt. Province' },
  { value: 57, label: 'RDO063', name: '063: Calapan, Oriental Mindoro' },
  { value: 58, label: 'RDO113A', name: '113A: West Davao City' },
  { value: 59, label: 'RDO084', name: '084: Tagbilaran City' },
  { value: 60, label: 'RDO108', name: '108: Kidapawan, North Cotabato' },
  { value: 61, label: 'RDO098', name: '098: Cagayan de Oro City' },
  { value: 62, label: 'RDO112', name: '112: Tagum, Davao del Norte' },
  { value: 63, label: 'RDO097', name: '097: Gingoog City' },
  { value: 64, label: 'RDO023', name: '023: Tuguegarao, Cagayan' },
  { value: 65, label: 'RDO113B', name: '113B: East Davao City' },
  { value: 66, label: 'RDO53A', name: '53A: Las Pinas City' },
  { value: 67, label: 'RDO089', name: '089: Ormoc City' },
  { value: 68, label: 'RDO067', name: '067: Legazpi City, Albay' },
  { value: 69, label: 'RDO049', name: '049: North Makati' },
  { value: 70, label: 'RDO092', name: '092: Pagadian City, Zamboanga del Sur' },
  { value: 71, label: 'RDO029', name: '029: Tondo - San Nicolas' },
  { value: 72, label: 'RDO033', name: '033: Intramuros-Ermita-Malate' },
  { value: 73, label: 'RDO041', name: '041: Mandaluyong' },
  { value: 74, label: 'RDO008', name: '008: Baguio City' },
  { value: 75, label: 'RDO078', name: '078: Binalbagan, Negros Occidental' },
  { value: 76, label: 'RDO069', name: '069: Virac, Catanduanes' },
  { value: 77, label: 'RDO032', name: '032: Quiapo-Sampaloc-San Miguel' },
  { value: 78, label: 'RDO081', name: '081: Cebu City North' },
  { value: 79, label: 'RDO005', name: '005: Alaminos, West Pangasinan' },
  { value: 80, label: 'RDO047', name: '047: East Makati' },
  { value: 81, label: 'RDO25A', name: '25A: Plaridel, Bulacan' },
  { value: 82, label: 'RDO43A', name: '43A: East Pasig' },
  { value: 83, label: 'RDO043', name: '043: Pasig' },
  {
    value: 84,
    label: 'RDO123',
    name: '123: Cebu Large Taxpayer District Office'
  },
  { value: 85, label: 'RDO068', name: '068: Sorsogon, Sorsogon' },
  { value: 86, label: 'RDO073', name: '073: San Jose, Antique' },
  { value: 87, label: 'RDO095', name: '095: Jolo, Sulu' },
  { value: 88, label: 'RDO53B', name: '53B: Muntinlupa City' },
  { value: 89, label: 'RDO23A', name: '23A: North Nueva Ecija' },
  { value: 90, label: 'RDO088', name: '088: Tacloban City' },
  { value: 91, label: 'RDO21B', name: '21B: South Pampanga' },
  { value: 92, label: 'RDO114', name: '114: Mati, Davao Oriental' },
  { value: 93, label: 'RDO009', name: '009: La Trinidad, Benguet' },
  { value: 94, label: 'RDO022', name: '022: Baler, Aurora' },
  { value: 95, label: 'RDO93B', name: '93B: Zamboanga Sibugay' },
  { value: 96, label: 'RDO058', name: '058: Batangas City' },
  { value: 97, label: 'RDO054', name: '054: Trece Martirez City' },
  { value: 98, label: 'RDO025', name: '025: Malolos, Bulacan' },
  { value: 99, label: 'RDO071', name: '071: Kalibo, Aklan' },
  { value: 100, label: 'RDO17B', name: '17B: Paniqui, Tarlac' },
  { value: 101, label: 'RDO113', name: '113: Davao City' },
  { value: 102, label: 'RDO087', name: '087: Catbalogan, Western Samar' },
  { value: 103, label: 'RDO031', name: '031: Sta. Cruz' },
  { value: 104, label: 'RDO061', name: '061: Gumaca, Quezon' },
  { value: 105, label: 'RDO111', name: '111: Koronadal, South Cotabato' },
  { value: 106, label: 'RDO040', name: '040: Cubao' },
  { value: 107, label: 'RDO082', name: '082: Cebu City South' },
  { value: 108, label: 'RDO080', name: '080: Mandaue City' },
  { value: 109, label: 'RDO060', name: '060: Lucena City' },
  { value: 110, label: 'RDO011', name: '011: Tabuk, Kalinga-Apayao' },
  { value: 111, label: 'RDO038', name: '038: North, Quezon City' },
  { value: 112, label: 'RDO115', name: '115: Digos, Davao del Sur' },
  { value: 113, label: 'RDO051', name: '051: Pasay City' },
  { value: 114, label: 'RDO077', name: '077: Bacolod City' },
  { value: 115, label: 'RDO020', name: '020: Balanga, Bataan' },
  { value: 116, label: 'RDO066', name: '066: Iriga City' },
  { value: 117, label: 'RDO056', name: '056: Calamba, Laguna' },
  { value: 118, label: 'RDO014', name: '014: Bayombong, Nueva Vizcaya' },
  {
    value: 119,
    label: 'RDO034',
    name: '034: Paco-Pandacan-Sta. Ana-San Andres'
  },
  { value: 120, label: 'RDO021', name: '021: City of San Fernando, Pampanga' },
  { value: 121, label: 'RDO007', name: '007: Bangued, Abra' },
  { value: 122, label: 'RDO099', name: '099: Malaybalay, Bukidnon' },
  { value: 123, label: 'RDO21A', name: '21A: North Pampanga' },
  { value: 124, label: 'RDO018', name: '018: Olongapo City' },
  { value: 125, label: 'RDO075', name: '075: Zarraga, Iloilo City' },
  { value: 126, label: 'RDO001', name: '001: Laoag City, Ilocos Norte' },
  { value: 127, label: 'RDO109', name: '109: Tacurong, Sultan Kudarat' },
  { value: 128, label: 'RDO045', name: '045: Marikina' },
  { value: 129, label: 'RDO046', name: '046: Cainta-Taytay' },
  { value: 130, label: 'RDO54B', name: '54B: Bacoor, North Cavite' },
  { value: 131, label: 'RDO044', name: '044: Taguig-Pateros' },
  { value: 132, label: 'RDO102', name: '102: Marawi City' },
  { value: 133, label: 'RDO107', name: '107: Cotabato City' },
  { value: 134, label: 'RDO083', name: '083: Talisay Cebu' },
  {
    value: 135,
    label: 'RDO54A',
    name: '54A: Trece Martirez City, South Cavite'
  },
  {
    value: 136,
    label: 'RDO116',
    name: '116: Large Taxpayers Assistance Division'
  },
  { value: 137, label: 'RDO121', name: '121: Excise LT Audit Division 1' },
  { value: 138, label: 'RDO124', name: '124: Excise LT Audit Division II' },
  {
    value: 139,
    label: 'RDO125',
    name: '125: Large Taxpayers Assistance Division'
  },
  {
    value: 140,
    label: 'RDO126',
    name: '126: Large Taxpayers Assistance Division'
  },
  { value: 141, label: 'RDO127', name: '127: Large Taxpayer Division Davao' },
  { value: 142, label: 'RDO132', name: '132: East Davao City' }
];

export const GUID_EMPTY = '00000000-0000-0000-0000-000000000000';

export const ATCCode: Array<{ value: number; label: string; name: string }> = [
  { value: 0, label: 'NotApplicable', name: 'N/A' },
  { value: 1, label: 'VQ010', name: 'Mining and Quarrying' },
  { value: 2, label: 'VM040', name: 'Tobacco' },
  { value: 3, label: 'VM110', name: 'Alchohol' },
  { value: 4, label: 'VM120', name: 'Petroleum' },
  { value: 5, label: 'VM130', name: 'Automobiles' },
  { value: 6, label: 'VM140', name: 'Non-Essentials(Excisable Goods)' },
  { value: 7, label: 'VM030', name: 'Cement' },
  { value: 8, label: 'VM020', name: 'Food Products and Beverages' },
  { value: 9, label: 'VM150', name: 'Pharmaceuticals' },
  { value: 10, label: 'VM050', name: 'Flour' },
  { value: 11, label: 'VM160', name: 'Sugar' },
  { value: 12, label: 'VM100', name: 'Pesticides' },
  { value: 13, label: 'VM010', name: 'Others(General)' },
  { value: 14, label: 'VB113', name: 'Non Life Insurance' },
  {
    value: 15,
    label: 'VB102',
    name: 'Lending Investors/Dealer in securities/Pawnshops/Pre-need Co'
  },
  { value: 16, label: 'VC010', name: 'Construction' },
  { value: 17, label: 'VT010', name: 'Wholesales & Retail' },
  { value: 18, label: 'VB100', name: 'Hotels, Motels' },
  { value: 19, label: 'VB101', name: 'Restaurants, Caterers' },
  { value: 20, label: 'VB105', name: 'Land Transport-Cargo' },
  { value: 21, label: 'VB106', name: 'Domestic Ocean Going Vessels' },
  { value: 22, label: 'VB107', name: 'Inter Island Shipping Vessels' },
  { value: 23, label: 'VB108', name: 'Air Transpo-Cargo' },
  { value: 24, label: 'VB109', name: 'Telephone and Telegraph' },
  { value: 25, label: 'VB111', name: 'Radio/TV Broadcasting' },
  { value: 26, label: 'VB112', name: 'Other Franchise' },
  { value: 27, label: 'VP100', name: 'Sale Of Real Property' },
  { value: 28, label: 'VP101', name: 'Lease Of Real Property' },
  { value: 29, label: 'VP102', name: 'Sale/Lease Of Intangible Property' },
  { value: 30, label: 'VD010', name: 'Compulsary Social Security' },
  {
    value: 31,
    label: 'VH010',
    name: 'Other Community Social And Personal Service Activity'
  },
  { value: 32, label: 'VS010', name: 'Storage & Warehousing' },
  { value: 33, label: 'VB010', name: 'Business Services(In General)' },
  { value: 34, label: 'VI010', name: 'Importation Of Goods' }
];

export const QNE_TEMPLATE: Array<{ value: number; label: string }> = [
  { value: 0, label: 'One (1) Column' },
  { value: 1, label: 'Two (2) Columns' }
];

export const EmailSettingsFormTypeContent: Array<{
  value: number;
  label: string;
}> = [
  { value: 0, label: 'E-Statement' },
  { value: 1, label: 'E-Invoicing' },
  { value: 2, label: 'Before Due Date Payment Reminder' },
  { value: 3, label: 'On Due Date Payment Reminder' },
  { value: 4, label: 'After Due Date Payment Reminder' },
  { value: 5, label: 'Report' },
  { value: 6, label: 'Transaction Send Notification' },
  { value: 7, label: 'Transaction Send Payment Collection' }
];

export const WhatsappSettingsFormTypeContent: Array<{
  value: number;
  label: string;
}> = [
  { value: 0, label: 'Report' },
  { value: 1, label: 'Transaction Send Notification' },
  { value: 2, label: 'Transaction Payment Collection' }
];

export const EINVOICE_STOCK_CLASSIFICATION: Array<{
  value: string;
  name: string;
}> = [
  { value: '001', name: 'Breastfeeding equipment' },
  { value: '002', name: 'Child care centres and kindergartens fees' },
  { value: '003', name: 'Computer, smartphone or tablet' },
  { value: '004', name: 'Consolidated E-Invoice ' },
  {
    value: '005',
    name: 'Construction materials (as defined in Lembaga Pembangunan Industri Pembinaan Malaysia Act 1994)'
  },
  { value: '006', name: 'Disbursement ' },
  { value: '007', name: 'Donation' },
  { value: '008', name: 'e-Commerce - E-Invoice to buyer / purchaser' },
  {
    value: '009',
    name: 'e-Commerce - Self-billed E-Invoice to seller, logistics, etc. '
  },
  { value: '010', name: 'Education fees' },
  { value: '011', name: 'Goods on consignment (Consignor)' },
  { value: '012', name: 'Goods on consignment (Consignee)' },
  { value: '013', name: 'Gym membership' },
  { value: '014', name: 'Insurance - Education and medical benefits' },
  { value: '015', name: 'Insurance - Takaful or life insurance' },
  { value: '016', name: 'Interest and financing expenses' },
  { value: '017', name: 'Internet subscription' },
  { value: '018', name: 'Land and building' },
  {
    value: '019',
    name: 'Medical examination for learning disabilities and early intervention or rehabilitation treatments of learning disabilities'
  },
  { value: '020', name: 'Medical examination or vaccination expenses' },
  { value: '021', name: 'Medical expenses for serious diseases' },
  { value: '022', name: 'Others' },
  {
    value: '023',
    name: 'Petroleum operations (as defined in Petroleum (Income Tax) Act 1967)'
  },
  {
    value: '024',
    name: 'Private retirement scheme or deferred annuity scheme '
  },
  { value: '025', name: 'Purchase of motor vehicle' },
  {
    value: '026',
    name: 'Purchase or subscription of books / journals / magazines / newspapers / other similar publications'
  },
  { value: '027', name: 'Reimbursement ' },
  { value: '028', name: 'Rental of motor vehicle' },
  { value: '029', name: 'Rental or purchase of EV charging facilities ' },
  { value: '030', name: 'Repair and maintenance' },
  { value: '031', name: 'Research and development ' },
  { value: '032', name: 'Self-billed - Foreign income ' },
  { value: '033', name: 'Self-billed - Betting and gaming ' },
  { value: '034', name: 'Self-billed - Importation of goods ' },
  { value: '035', name: 'Self-billed - Importation of services' },
  { value: '036', name: 'Self-billed - Others' },
  {
    value: '037',
    name: 'Self-billed - Payment to agents, dealers or distributors '
  },
  {
    value: '038',
    name: 'Sports equipment, rental / entry fees for sports facilities or registration in sports competition '
  },
  { value: '039', name: 'Supporting equipment for disabled person' },
  { value: '040', name: 'Voluntary contribution to approved provident fund' },
  { value: '041', name: 'Dental examination or treatment' },
  { value: '042', name: 'Fertility treatment' },
  {
    value: '043',
    name: 'Treatment and home care nursing, daycare centres and residential care centers'
  },
  { value: '044', name: 'Vouchers, gift cards, loyalty points, etc' },
  {
    value: '045',
    name: 'Self-billed - Non-monetary payment to agents, dealers or distributors'
  }
];

export const EINVOICE_TYPE: Array<{ value: string; name: string }> = [
  { value: '01', name: 'Invoice' },
  { value: '02', name: 'Credit Note' },
  { value: '03', name: 'Debit Note' },
  { value: '04', name: 'Refund Note' },
  { value: '11', name: 'Self-billed Invoice' },
  { value: '12', name: 'Self-billed Credit Note' },
  { value: '13', name: 'Self-billed Debit Note' },
  { value: '14', name: 'Self-billed Refund Note' }
];

export const EINVOICE_PAYM: Array<{ value: string; name: string }> = [
  { value: '01', name: 'Cash' },
  { value: '02', name: 'Cheque' },
  { value: '03', name: 'Bank Transfer' },
  { value: '04', name: 'Credit Card' },
  { value: '05', name: 'Debit Card' },
  { value: '06', name: 'e-Wallet / Digital Wallet' },
  { value: '07', name: 'Digital Bank' },
  { value: '08', name: 'Others' }
];

export const EInvoiceValidationControl = {
  FollowSettings: 0,
  FullCompliance: 1,
  DeferredCompliance: 2,
  NonCompliance: 3
};

export const EInvoiceTransactionType = {
  NA: 0,
  Agent: 1,
  ForeignSuppliers: 2,
  RecipientOfTheDistribution: 3,
  Merchant: 4,
  RecipientOfThePayout: 5,
  IndividualTaxpayer: 6,
  RecipientOfTheInterestPayment: 8,
  All: 99
};
export const TransactionType = {
  INV: 1,
  AROR: 2,
  ARRefund: 3,
  ARDN: 4,
  ARCN: 5,
  BIL: 6,
  APPV: 7,
  APRefund: 8,
  APDN: 9,
  APCN: 10,
  QT: 11,
  SO: 12,
  SINV: 13,
  DO: 14,
  CS: 15,
  SDN: 16,
  SCN: 17,
  PR: 18,
  PO: 19,
  GRN: 20,
  PINV: 21,
  CP: 22,
  PDN: 23,
  PCN: 24,
  CSR: 25,
  CPF: 26
};

export const PtAtcCode: Array<{ value: string; desc: string; rate: number }> = [
  {
    value: 'PT 010',
    desc: 'Persons exempt from VAT under Sec. 109(BB) (Sec. 116)',
    rate: 0.03
  },
  {
    value: 'PT 040',
    desc: 'Domestic carriers and keepers of garages (Sec. 117)',
    rate: 0.03
  },
  { value: 'PT 041', desc: 'International Carriers (Sec. 118)', rate: 0.03 },
  {
    value: 'PT 060',
    desc: 'Franchises on gas and water utilities (Sec. 119)',
    rate: 0.02
  },
  {
    value: 'PT 070',
    desc: 'Franchises on radio/TV broadcasting  companies whose annual gross receipts  do not exceed P10 M (Sec. 119) ',
    rate: 0.03
  },
  {
    value: 'PT 090',
    desc: 'Overseas dispatch, message or conversation originating from the Philippines (Sec. 120)',
    rate: 0.1
  },
  { value: 'PT 140', desc: 'Cockpits (Sec. 125)', rate: 0.18 },
  {
    value: 'PT 150',
    desc: 'Tax on amusement places, such as cabarets, night and day clubs, videoke bars, karaoke bars, karaoke television, karaoke boxes, music lounges and other similar establishments (Sec. 125)',
    rate: 0.18
  },
  { value: 'PT 160', desc: 'Boxing Exhibition (Sec. 125)', rate: 0.1 },
  {
    value: 'PT 170',
    desc: 'Professional Basketball Games (Sec. 125)',
    rate: 0.15
  },
  { value: 'PT 180', desc: 'Jai-alai and Race Tracks (Sec. 125)', rate: 0.3 },
  {
    value: 'PT 105',
    desc: 'Tax on Banks and Non-Bank Financial Intermediaries Performing Quasi-Banking Functions (Sec. 121) - Maturity period is five (5) years or less',
    rate: 0.05
  },
  {
    value: 'PT 101',
    desc: 'Tax on Banks and Non-Bank Financial Intermediaries Performing Quasi-Banking Functions (Sec. 121) - Maturity period is more than five (5) years',
    rate: 0.01
  },
  {
    value: 'PT 102',
    desc: 'On dividends and equity shares and net income of subsidiaries',
    rate: 0
  },
  {
    value: 'PT 103',
    desc: 'On royalties, rentals of property, real or personal, profits from exchange and all other gross income',
    rate: 0.07
  },
  {
    value: 'PT 104',
    desc: 'On net trading gains within the taxable year on foreign currency, debt securities, derivatives and other financial instruments',
    rate: 0.07
  },
  {
    value: 'PT 113',
    desc: 'Tax on Other Non-Bank Financial Intermediaries not Performing Quasi-Banking Functions (Sec. 122) - Maturity period is five (5) years or less',
    rate: 0.05
  },
  {
    value: 'PT 114',
    desc: 'Tax on Other Non-Bank Financial Intermediaries not Performing Quasi-Banking Functions (Sec. 122) - Maturity period is more than five (5) years',
    rate: 0.01
  },
  {
    value: 'PT 115',
    desc: 'From all other items treated as gross income under the code',
    rate: 0.05
  },
  { value: 'PT 120', desc: 'Life Insurance Premiums (Sec. 123)', rate: 0.02 },
  { value: 'PT 130', desc: 'Insurance Agents', rate: 0.04 },
  {
    value: 'PT 132',
    desc: 'Owners of property obtaining insurance directly with foreign insurance companies',
    rate: 0.05
  }
];

export const RecurringTransactions: Array<{
  group: string;
  aclKey: string;
  code: string;
  value: string;
  label: string;
}> = [
  {
    group: 'GL',
    aclKey: 'JV',
    code: 'JV',
    value: 'Journal',
    label: 'Journal Voucher'
  },
  {
    group: 'GL',
    aclKey: 'PV',
    code: 'PV',
    value: 'Payment',
    label: 'Payment Voucher'
  },
  {
    group: 'GL',
    aclKey: 'OR',
    code: 'RV',
    value: 'Receipt',
    label: 'Receipt Voucher'
  },
  {
    group: 'AR',
    aclKey: 'INV',
    code: 'INV',
    value: 'Invoice',
    label: 'Invoice'
  },
  {
    group: 'AR',
    aclKey: 'AROR',
    code: 'OR',
    value: 'ARReceipt',
    label: 'Receive Payments'
  },
  {
    group: 'SALE',
    aclKey: 'SINV',
    code: 'INV',
    value: 'SalesInvoice',
    label: 'Sales Invoice'
  },
  {
    group: 'SALE',
    aclKey: 'CS',
    code: 'CS',
    value: 'CashSale',
    label: 'Cash Sales'
  },
  { group: 'AP', aclKey: 'BIL', code: 'BIL', value: 'Bill', label: 'Bills' },
  {
    group: 'AP',
    aclKey: 'APPV',
    code: 'PAY-BIL',
    value: 'APPayment',
    label: 'Pay Bills'
  },
  {
    group: 'PURCHASE',
    aclKey: 'PINV',
    code: 'INV',
    value: 'PurchaseInvoice',
    label: 'Purchase Invoice'
  },
  {
    group: 'PURCHASE',
    aclKey: 'CP',
    code: 'CP',
    value: 'CashPurchase',
    label: 'Cash Purchases'
  }
];

export const COMPANYPROFILE_IDTYPE = ['BRN', 'NRIC', 'PASSPORT'];
